/**
 * Google Analytics (gtag.js) bootstrap.
 *
 * The measurement id is NOT baked into index.html. One bundle ships to every
 * environment (see runtimeConfig), so a hard-coded id would report QA and Prod
 * traffic into the same property — and there is a different id per environment.
 * It arrives in `/config.json`, which the deploy pipeline writes per env.
 *
 * No id means no tag: nothing is injected, no request to Google is made. That is
 * the default, so localhost and any environment nobody has configured stay out
 * of the analytics property rather than polluting it.
 *
 * What this sends is what the standard snippet sends: a page_view for the
 * document that loaded, carrying the URL and referrer. Our URLs hold workflow
 * and step GUIDs, never advisor-entered values, and nothing here reads form
 * state — worth keeping true given the forms collect SIN and date of birth.
 */

const SCRIPT_ID = 'ga-gtag';

/** `gtag` pushes its own `arguments` object, so this mirrors the vendor snippet. */
type GtagArgs = IArguments;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Inject gtag.js for `measurementId`.
 *
 * Safe to call more than once and safe to call with nothing: a blank or missing
 * id is the "analytics off" signal, not an error to report.
 */
export function initGoogleAnalytics(measurementId: string | undefined | null): void {
  const id = measurementId?.trim();
  if (!id) return;
  // React 18 StrictMode double-invokes effects in development, and a remount
  // must not add a second tag — that would double-count every page view.
  if (document.getElementById(SCRIPT_ID)) return;

  window.dataLayer = window.dataLayer || [];
  // Deliberately `arguments`, not a rest parameter: gtag.js reads the pushed
  // value as an arguments object and a real array does not behave identically.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as unknown as GtagArgs);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', id);

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}
